"use client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Drawer, Typography, useTheme } from "@mui/material";
import { useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import LessonCheckIcon from "./LessonCheckIcon";
import LessonLockIcon from "./LessonLockIcon";
import ProgressRing from "./ProgressRing";

interface LessonItem {
  id: number;
  title: string;
  completed?: boolean;
  locked?: boolean;
}

interface ChapterItem {
  id: number;
  title: string;
  lessons: LessonItem[];
  progress?: number;
  completedCount?: number;
  totalCount?: number;
}

interface CourseBottomSheetProps {
  catalog: ChapterItem[];
  open: boolean;
  onClose: () => void;
}

/**
 * 课程目录底部弹出组件
 * 专为移动端设计的课程目录显示界面
 */
export default function CourseBottomSheet({ catalog, open, onClose }: CourseBottomSheetProps) {
  const t = useTranslations("CitadelCourse");
  const router = useRouter();
  const params = useParams();
  const theme = useTheme();
  const currentLessonId = params.lessonId ? Number(params.lessonId) : null;

  const [openChapters, setOpenChapters] = useState<{ [key: number]: boolean }>({});

  // 处理课程点击事件
  const handleLessonClick = (lessonId: number) => {
    router.push(`/products/citadel/course/${lessonId}`);
    onClose(); // 点击后自动关闭
  };

  const handleToggle = (idx: number) => {
    setOpenChapters((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // 初始化展开状态
  useEffect(() => {
    if (!open) return;

    let targetChapterIdx = -1;
    if (currentLessonId) {
      targetChapterIdx = catalog.findIndex((chapter) =>
        chapter.lessons.some((lesson) => lesson.id === currentLessonId)
      );
    }

    if (targetChapterIdx === -1) {
      targetChapterIdx = 0;
    }

    const obj: { [key: number]: boolean } = {};
    catalog.forEach((_, idx) => {
      obj[idx] = idx === targetChapterIdx;
    });

    setOpenChapters(obj);
  }, [catalog, currentLessonId, open]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
          maxHeight: "85vh",
          bgcolor: "background.default",
          color: "text.primary",
        },
      }}>
      {/* 顶部拖拽指示器 */}
      <Box sx={{ textAlign: "center", py: 1 }}>
        <Box
          sx={{
            width: "2rem",
            height: "0.25rem",
            bgcolor: "divider",
            borderRadius: "0.125rem",
            mx: "auto",
          }}
        />
      </Box>

      {/* 滚动内容区域 */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          pt: 1,
          pb: 2,
        }}>
        {catalog.map((chapter, idx) => {
          const completedCount = chapter.lessons.filter((lesson: LessonItem) => lesson.completed).length;
          const totalCount = chapter.lessons.length;
          const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
          const isExpanded = openChapters[idx] !== false;

          return (
            <Box
              key={chapter.id}
              sx={{
                mb: 3,
                bgcolor: "transparent",
                borderRadius: 0,
                overflow: "hidden",
              }}>
              {/* 章节头部 */}
              <Box
                onClick={() => handleToggle(idx)}
                sx={{
                  py: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  borderRadius: "0.5rem",
                }}>
                <Box sx={{ flex: 1 }}>
                  {/* 标题行 - 包含箭头和标题 */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    {/* 展开/收起图标 */}
                    <Box sx={{ flexShrink: 0, mt: "-0.125rem" }}>
                      {isExpanded ? (
                        <ExpandMoreIcon sx={{ color: "text.primary", fontSize: "1.275rem" }} />
                      ) : (
                        <ExpandLessIcon
                          sx={{ color: "text.primary", transform: "rotate(90deg)", fontSize: "1.275rem" }}
                        />
                      )}
                    </Box>

                    {/* 标题和进度信息列 */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "var(--font-size-xs)",
                          color: "text.primary",
                          lineHeight: 1.6,
                          mb: 0.5,
                        }}>
                        {chapter.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "var(--font-size-xs)",
                          color: "text.secondary",
                        }}>
                        {t("sidebar.lessonProgress", {
                          completed: completedCount,
                          total: totalCount,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 进度环 */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <ProgressRing progress={progress} size={48} strokeWidth={6} />
                </Box>
              </Box>

              {/* 课程列表 */}
              <Box
                sx={{
                  overflow: "hidden",
                  transition: "max-height 0.3s ease-in-out",
                  maxHeight: isExpanded ? "1000px" : 0,
                }}>
                <Box sx={{ mt: 0.5 }}>
                  {chapter.lessons.map((lesson, lessonIdx) => {
                    const isCurrentLesson = lesson.id === currentLessonId;
                    return (
                      <Box
                        key={lesson.id}
                        onClick={() => !lesson.locked && handleLessonClick(lesson.id)}
                        sx={{
                          p: 1.5,
                          my: 1,
                          borderRadius: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: !lesson.completed && !lesson.locked ? 0 : 1,
                          cursor: lesson.locked ? "default" : "pointer",
                          bgcolor: "background.paper",
                        }}>
                        {/* 课程状态图标 */}
                        <Box sx={{ flexShrink: 0 }}>
                          {lesson.locked ? (
                            <LessonLockIcon />
                          ) : lesson.completed ? (
                            <LessonCheckIcon
                              size="1rem"
                              style={{
                                color: isCurrentLesson ? "inherit" : theme.palette.success.main,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* 课程标题 */}
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "var(--font-size-xs)",
                            color:
                              !lesson.completed && !lesson.locked
                                ? "text.primary"
                                : lesson.completed
                                ? "text.secondary"
                                : "text.tertiary",
                            lineHeight: 1.6,
                          }}>
                          {lesson.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* 底部安全区域 */}
        <Box sx={{ height: "2rem" }} />
      </Box>
    </Drawer>
  );
}
